import { useState, useEffect, useCallback, useRef } from 'react'
import { apiCallable } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

interface TelegramLinkState {
  linked: boolean
  loading: boolean
  linkCode: string | null
  botDeepLink: string | null
  generating: boolean
}

// How often to poll the backend for the current link status.
const POLL_INTERVAL_MS = 5000

export function useTelegramLink() {
  const { user } = useAuth()
  const [state, setState] = useState<TelegramLinkState>({
    linked: false,
    loading: true,
    linkCode: null,
    botDeepLink: null,
    generating: false,
  })

  const fetchStatus = useCallback(async () => {
    try {
      const fn = apiCallable('getTelegramLinkStatus')
      const res = await fn({})
      const data = res.data as { linked?: boolean }
      setState((prev) => ({ ...prev, linked: !!data.linked, loading: false }))
    } catch (error) {
      console.error('Failed to fetch Telegram link status:', error)
      setState((prev) => ({ ...prev, loading: false }))
    }
  }, [])

  const fetchRef = useRef(fetchStatus)
  fetchRef.current = fetchStatus

  // Fetch the current link status once whenever the signed-in user changes.
  useEffect(() => {
    if (!user?.uid) {
      setState((prev) => ({ ...prev, loading: false, linked: false }))
      return
    }
    setState((prev) => ({ ...prev, loading: true }))
    fetchRef.current()
  }, [user?.uid])

  // Poll only while a link is actively being established: a code has been
  // generated but the account isn't linked yet, so the UI can flip to "linked"
  // once the user completes the flow in Telegram. Idle mounts that only read
  // the status (e.g. the device-list button) never generate a code, so they
  // fetch once on load and never poll. Callers can refetch on demand instead.
  const shouldPoll = !!user?.uid && !!state.linkCode && !state.linked
  useEffect(() => {
    if (!shouldPoll) return
    const interval = setInterval(() => fetchRef.current(), POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [shouldPoll])

  const generateLinkCode = useCallback(async () => {
    setState(prev => ({ ...prev, generating: true }))
    try {
      const fn = apiCallable('generateTelegramLinkCode')
      const result = await fn({})
      const data = result.data as { code: string; botDeepLink: string }
      setState(prev => ({
        ...prev,
        linkCode: data.code,
        botDeepLink: data.botDeepLink,
        generating: false,
      }))
      return data
    } catch (error) {
      console.error('Failed to generate link code:', error)
      setState(prev => ({ ...prev, generating: false }))
      throw error
    }
  }, [])

  const unlinkTelegram = useCallback(async () => {
    try {
      const fn = apiCallable('unlinkTelegram')
      await fn({})
      setState(prev => ({
        ...prev,
        linked: false,
        linkCode: null,
        botDeepLink: null,
      }))
    } catch (error) {
      console.error('Failed to unlink Telegram:', error)
      throw error
    }
  }, [])

  return {
    linked: state.linked,
    loading: state.loading,
    linkCode: state.linkCode,
    botDeepLink: state.botDeepLink,
    generating: state.generating,
    generateLinkCode,
    unlinkTelegram,
    refetch: fetchStatus,
  }
}
